import * as React from 'react'
import { useAuth } from './context/AuthProvider'
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
  Outlet,
  useLocation,
} from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { PrivateRoute } from './PrivateRoute'
import * as RT from './config/routeType'
import {
  MAIN as MAIN_ROUTE,
  AUTHENTICATION_ROUTE,
  LOGIN_ROUTE,
  ADMINISTRATION_DEFAULT_TAB,
  TERRITORIAL_MANAGEMENT_DEFAULT_TAB,
} from './config/routes'
import { PUBLIC_SIGNUP_ENABLED } from './config/featureFlags'
import NotFoundPage from './containers/NotFoundPage'
import AppPage from './containers/AppPage'
import GenerateReportContainer from './containers/GenerateReport/GenerateReportContainer'
import ManagementTable from './containers/ManagementTable'
import CheckConnection from './containers/CheckConnection'
import ForgotPasswordPage from './containers/ForgotPasswordPage'
import ProfilePage from './containers/ProfilePage'
import SignUpPage from './containers/SignUpPage'
import AuthorizeAccountPage from './containers/AuthorizeAccountPage'
import RegisterTreeWizardViewV2 from './containers/RegisterTreePage'
import EditTreeWizardView from './containers/EditTreePage'
import ResetPasswordPage from './containers/ResetPasswordPage'
import AuthPage from './containers/LoginPage/LoginPage'
import Login from './components/Login'
import FullScreenLoading from './components/FullScreenLoading'
import MaintenanceOverlay from './components/MaintenanceOverlay'
import PlatformUpdateNotice from './components/PlatformUpdateNotice'
import { clearRedirectError } from './redux/slices/ServerStatusSlice'
import HomePage from './containers/HomePage'
import IndexComponent from './components/HomePageComponents/IndexComponent/IndexComponent'
import TeamComponent from './components/HomePageComponents/TeamComponent/TeamComponent'
import NoticiaComponent from './components/HomePageComponents/NoticiaComponent/NoticiaComponent'
import ContactPage from './containers/ContactPage'
import PublicationsPage from './containers/PublicationsPage'
import TerritorialManagement from './containers/TerritorialManagement'
import TenantProtectedRoute from './components/TenantProtectedRoute'
import { detectCityFromURL } from './utils/cityDetector'
import CitiesProvider from './context/CitiesProvider'
import { useCities } from './context/CitiesContext'
import { buildCantonUrl } from './utils/cantonUrl'

// SmartRoute: maneja /:iso2 — puede ser país (renderiza AppPage sin cantón)
// o slug legacy de cantón (redirige a /:iso2/:canton)
function SmartRoute({ auth }) {
  const { iso2 } = useParams()
  const { pathname } = useLocation()
  const { cantones, getUniqueCountries, loading: citiesLoading } = useCities()

  if (citiesLoading) return null

  const pathSegments = pathname.split('/').filter(Boolean)
  const canResolveTreeRoute =
    (pathSegments.length === 3 &&
      ![RT.REGISTER_TREE, RT.MANAGEMENT_TREE].includes(pathSegments[2])) ||
    (pathSegments.length === 4 && pathSegments[2] === RT.MANAGEMENT_TREE)

  const countries = getUniqueCountries()
  const isValidCountry = countries.some(c => c.iso2.toLowerCase() === iso2.toLowerCase())

  if (isValidCountry) {
    const normalized = iso2.toLowerCase()
    if (iso2 !== normalized) return <Navigate to={`/${normalized}`} replace />
    // Mismo AppPage — sin cantón activo, sin árboles, dropdown abierto automáticamente
    return <AppPage {...auth} />
  }

  // Legacy: el segmento es un slug de cantón (ej. /loja)
  const canton = cantones.find(c => c.slug === iso2)
  if (canton?.countryIso2) {
    return <Navigate to={`/${canton.countryIso2.toLowerCase()}/${canton.slug}`} replace />
  }

  if (canResolveTreeRoute) return <AppPage {...auth} />

  return <NotFoundPage />
}

function AppRoutes() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const auth = useAuth()
  const {
    isAuthenticated: isAuth,
    isAdmin,
    loading,
    loadingStage,
    primaryCantonSlug,
  } = auth
  const city = detectCityFromURL(pathname)

  // Usar ciudades del contexto
  const { getDefaultCity, getEnabledCantones, loading: citiesLoading } = useCities()
  const defaultCitySlug = getDefaultCity()?.slug || 'loja'
  const cantones = getEnabledCantones()

  const errorType = useSelector(state => state.serverStatus.redirectType)
  const maintenanceActive = useSelector(state => state.serverStatus.maintenance.active)

  React.useEffect(() => {
    if (errorType) {
      navigate('/error', { state: { type: errorType } })
      dispatch(clearRedirectError())
    }
  }, [errorType, navigate, dispatch])

  if (maintenanceActive) {
    return <MaintenanceOverlay />
  }

  // Mostrar loading mientras se cargan las ciudades
  if (citiesLoading) {
    return <FullScreenLoading message="Cargando configuración..." showIcon={true} />
  }

  return (
    <>
      <CheckConnection />
      <PlatformUpdateNotice />
      <main>
        {loading && <FullScreenLoading message={loadingStage} showIcon={true} />}
        <Routes>
          <Route path={MAIN_ROUTE} element={<HomePage isAuth={isAuth} />}>
            {/* Rutas públicas generales*/}
            <Route index element={<IndexComponent isAuth={isAuth} />} />
            <Route path={RT.TEAM} element={<TeamComponent />} />
            <Route path={RT.PUBLICATIONS} element={<PublicationsPage />} />
            <Route path={RT.NEWS} element={<NoticiaComponent />} />
            <Route path={RT.CONTACT} element={<ContactPage />} />

            {/* /:iso2 — padre persistente para /ec, /ec/loja, etc.
                SmartRoute valida el iso2 y renderiza AppPage (mapa).
                Al navegar entre países o cantones el mapa NO se remonta. */}
            <Route path=":iso2" element={<SmartRoute auth={auth} />}>
              {/* /ec (sin cantón): AppPage muestra mapa centrado en el país */}
              <Route index element={null} />
              {/* /ec/loja y sub-rutas: AppPage detecta city desde la URL */}
              <Route path=":canton">
                <Route index element={null} />
                <Route path=":treeId" element={null} />
                <Route
                  element={
                    <PrivateRoute
                      isAllowed={isAuth}
                      redirectTo={AUTHENTICATION_ROUTE}
                      loading={loading}
                    />
                  }>
                  <Route element={<TenantProtectedRoute />}>
                    <Route
                      path={RT.REGISTER_TREE}
                      element={<RegisterTreeWizardViewV2 {...auth} />}
                    />
                    <Route path={RT.MANAGEMENT_TREE} element={<Outlet />}>
                      <Route path=":treeId" element={<EditTreeWizardView />} />
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Route>
            {/* Autenticación visible solo cuando no esta logueado */}
            {/* prettier-ignore */}
            <Route
              path={RT.AUTHENTICATION}
              element={<PrivateRoute isAllowed={!isAuth} redirectTo={buildCantonUrl(primaryCantonSlug || city || defaultCitySlug, cantones)} loading={loading}><AuthPage /></PrivateRoute>}>
              <Route path={RT.AUTH} element={<Login icon={false} modal={false} />} />
              <Route path={RT.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
              <Route
                path={RT.REGISTER_USER}
                element={PUBLIC_SIGNUP_ENABLED ? <SignUpPage /> : <Navigate to={LOGIN_ROUTE} replace />}
              />

            </Route>
            <Route
              path={`${RT.AUTH_RESET_PASSWORD}/:token`}
              element={<AppPage {...auth} hideMap={true} />}>
              <Route index element={<ResetPasswordPage />} />
            </Route>
            {/* Perfil visible solo cuando esta logueado */}
            <Route
              element={
                <PrivateRoute
                  isAllowed={isAuth}
                  redirectTo={RT.AUTHENTICATION}
                  loading={loading}
                />
              }>
              {/* Rutas sin mapa compartido */}
              <Route path={RT.PROFILE} element={<AppPage {...auth} hideMap={true} />}>
                <Route index element={<ProfilePage />} />
              </Route>

              {/* Rutas Admin */}
              {/* prettier-ignore */}
              <Route element={<PrivateRoute isAllowed={isAdmin} loading={loading} redirectTo={buildCantonUrl(primaryCantonSlug || defaultCitySlug, cantones)} />}>
                <Route path={RT.ADMINISTRATION} element={<AppPage {...auth} hideMap={true} />}>
                  <Route index element={<Navigate to={ADMINISTRATION_DEFAULT_TAB} replace />} />
                  <Route path=":tab" element={<ManagementTable />} />
                  <Route path={RT.GENERATE_REPORT} element={<GenerateReportContainer />} />
                </Route>
                <Route path={RT.TERRITORIAL_MANAGEMENT} element={<AppPage {...auth} hideMap={true} />}>
                  <Route index element={<Navigate to={TERRITORIAL_MANAGEMENT_DEFAULT_TAB} replace />} />
                  <Route path=":tab" element={<TerritorialManagement />} />
                </Route>
              </Route>
            </Route>
            {/* prettier-ignore */}
            <Route path={`${RT.AUTH_AUTHORIZE_USER}/:token`} element={<AuthorizeAccountPage />} />
            <Route path={RT.NOT_FOUND_404} element={<NotFoundPage />} />
            {/* <Route path="informacion" element={<InformationPage />} /> */}
          </Route>
        </Routes>
      </main>
    </>
  )
}

export default function App() {
  return (
    <CitiesProvider>
      <AppRoutes />
    </CitiesProvider>
  )
}
